import { useCallback, useMemo, useEffect } from 'react';
import useSearcher from '../useSeacher';
import { Button, Input, List, Skeleton } from 'antd';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import { isEmpty, omit } from 'lodash';
import React from 'react';
import { FactoryListItem } from 'app/components/List';

export default function FactorySearcher(props) {
  const { onChange, multiple = true, otherSource = [], selected = [] } = props;
  const { loading, params, data, hasMore, updateParams, fetchData } =
    useSearcher({
      resourceName: 'factories',
      lazyLoad: true,
      transformParams: useCallback((currentParams) => {
        const defaultParams: {
          [field: string]: any;
        } = {
          current: currentParams.page,
          pageSize: currentParams.pageSize,
          status: 'active'
          // connectionStatus: 'connected',
        };
        if (currentParams.keywords) {
          defaultParams.searchBy = 'all';
          defaultParams.keywords = currentParams.keywords;
        }
        return defaultParams;
      }, []),
      hasMore: useCallback((current, pagination) => {
        return current.length < pagination.total;
      }, [])
    });

  const selectionFromOtherSource = useMemo(() => {
    if (Array.isArray(otherSource) && otherSource.length) {
      return otherSource.map((item) => {
        return {
          ...item,
          selected: selected.some(({ id }) => id === item.id)
        };
      });
    }
    return [];
  }, [otherSource, selected]);

  const allFactoryGroup = useMemo(() => {
    if (isEmpty(data)) {
      return [];
    }

    if (selectionFromOtherSource.length) {
      const productSelectedIds = selectionFromOtherSource.map(({ id }) => id);
      return data
        .filter(({ id }) => !productSelectedIds.includes(id))
        .map((item) => {
          return {
            ...item,
            selected: selected.some(({ id }) => id === item.id)
          };
        });
    }
    return data.map((item) => {
      return {
        ...item,
        selected: selected.some(({ id }) => id === item.id)
      };
    });
  }, [data, selectionFromOtherSource, selected]);

  const grouped = useMemo(() => {
    return [
      {
        key: 'product-selected-factory',
        title: `Selected Product's Factory`,
        type: 'group',
        items: selectionFromOtherSource
      },
      {
        key: 'all-factory',
        title: 'All Factory',
        type: 'group',
        items: allFactoryGroup
      }
    ];
  }, [allFactoryGroup, selectionFromOtherSource]);

  const handleSelect = (initialFactories, index) => {
    const newData = [...initialFactories];
    if (multiple) {
      newData.splice(index, 1, {
        ...newData[index],
        selected: !newData[index].selected
      });
    } else {
      newData.forEach((item, i) => {
        newData[i] = {
          ...item,
          selected: i === index
        };
      });
    }

    return newData;
  };

  const onToggleSelect = (index, groupKey) => {
    const groupedMap = new Map(
      grouped.map((group) => [group.key, group.items])
    );
    const newData = handleSelect(groupedMap.get(groupKey), index);

    if (multiple) {
      const prevData =
        groupKey === 'product-selected-factory'
          ? selectionFromOtherSource
          : allFactoryGroup;
      onChange(
        [...newData, ...prevData]
          .filter(({ selected: itemSelected }) => itemSelected)
          .map((item) => omit(item, 'selected'))
      );
    } else {
      onChange(
        newData
          .filter(({ selected: itemSelected }) => itemSelected)
          .map((item) => omit(item, 'selected'))
      );
    }
  };

  const handleSearchChange = (e) => {
    updateParams({ page: 1, keywords: e.target.value });
  };

  const renderItem = (item, record, index) => {
    return (
      <FactoryListItem
        key={item?.id ?? index}
        // className={cn(classes.listItem, {
        //   [classes.listItemSelected]: item.selected,
        // })}
        factoryId={item.factoryId}
        name={item.name}
        address={item.address}
        certifications={
          Array.isArray(item.factoryCertifications)
            ? item.factoryCertifications
                .filter(({ certificationType }) => !!certificationType)
                .map(({ certificationType }) => ({
                  id: certificationType.id,
                  name: certificationType.name
                }))
            : []
        }
        bordered={false}
        extra={
          <Button
            icon={item.selected ? <CheckOutlined /> : undefined}
            type="primary"
            ghost={!item.selected}
            onClick={(e) => {
              e.preventDefault();
              onToggleSelect(index, record.key);
            }}
          >
            {item.selected ? 'Selected' : 'Select'}
          </Button>
        }
      />
    );
  };

  useEffect(() => {
    // Fetch data when component mounted
    // Fetch data when keywords has changed.
    if (!loading && hasMore) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.keywords]);

  return (
    <div>
      <div>
        <Input
          size="large"
          placeholder="Search by factory name & ID"
          prefix={<SearchOutlined />}
          onChange={handleSearchChange}
        />
      </div>
      <InfiniteScroll
        next={() => {
          if (!loading) {
            fetchData();
          }
        }}
        hasMore={hasMore}
        dataLength={isEmpty(data) ? 0 : data.length}
        height={450}
        loader={<Skeleton paragraph={{ rows: 5 }} active></Skeleton>}
      >
        <List
          locale={!loading ? undefined : { emptyText: <React.Fragment /> }}
          dataSource={grouped.filter(({ items }) => !!items.length)}
          renderItem={(record) => {
            if (record.type === 'group') {
              return (
                <React.Fragment key={record.key}>
                  <List.Item>{record.title}</List.Item>
                  {record.items.map((item, index) => {
                    return renderItem(item, record, index);
                  })}
                </React.Fragment>
              );
            }
            return null;
          }}
        />
      </InfiniteScroll>
    </div>
  );
}
